import { classnames, PillButton, SkeletonLine, Text, TextField } from '@terveystalo/design-system-react-components';
import { ApiModelType } from 'model/api';
import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AnswerChoice, ownApiAnswerWithValidation, SymptomDuration } from 'utils/types/common';
import { assertExhausted } from 'utils/types/misc';

type Props = {
  question: ApiModelType['Question'] | undefined;
  handleAnswerUpdate: (answer: ownApiAnswerWithValidation) => void;
  loading: boolean;
  isNotLast?: boolean;
  error: boolean;
};

const notLastStyles = classnames('pb-2', 'border-b-xxs', 'border-zodiac-black-15');
const pillButtonBaseStyle = classnames('mr-1', 'mt-0.5');

export const Question: FC<Props> = ({ question, handleAnswerUpdate, loading, isNotLast, error }) => {
  const { t } = useTranslation();
  const [answer, setAnswer] = useState<AnswerChoice | undefined>();
  const [symptomDuration, setSymptomDuration] = useState<SymptomDuration | undefined>();
  const [symptomInfo, setSymptomInfo] = useState<string | undefined>();
  const [isValid, setIsValid] = useState<boolean>(false);

  const onClickAnswer = function (question: ApiModelType['Question'], answer: AnswerChoice) {
    let valid = false;

    if (!question.id) return;

    if (answer === AnswerChoice.no || answer === AnswerChoice.dontKnow) {
      valid = true;
      // Only yes answer has additional information
      setSymptomDuration(undefined);
      setSymptomInfo(undefined);
    } else if (answer === AnswerChoice.yes) {
      if (question.askDurationOfSymptom || question.askMoreInformationAboutSymptom) {
        valid = false; // We need more information
      } else {
        valid = true;
      }
    }
    setAnswer(answer);
    setIsValid(valid);
  };

  const onClickDuration = (question: ApiModelType['Question'], duration: SymptomDuration) => {
    if (question.askMoreInformationAboutSymptom && !symptomInfo) {
      setIsValid(false); // We still need more information
    } else {
      setIsValid(true);
    }
    setSymptomDuration(duration);
  };

  const onClickMoreInformation = (question: ApiModelType['Question'], text: string) => {
    if (question.askDurationOfSymptom && !symptomDuration) {
      setIsValid(false); // We still need more information
    } else if (text !== '') {
      setIsValid(true);
    } else {
      setIsValid(false);
    }
    setSymptomInfo(text);
  };

  /*
  Converts own symptom duration enum to translated answer string for api.
  Has to start with use since it uses translation hook
*/
  function ownToApiSymptomDuration(duration: SymptomDuration | undefined): string | undefined {
    switch (duration) {
      case SymptomDuration.underOne:
        return t('under_one_day');
      case SymptomDuration.oneToTwo:
        return t('one_to_two_days');
      case SymptomDuration.twoToThree:
        return t('two_to_three_days');
      case SymptomDuration.overThree:
        return t('over_three_days');
      case undefined:
        return undefined;
      default:
        return assertExhausted();
    }
  }

  useEffect(() => {
    // Dont add loading dummy to answers
    if (!loading) {
      handleAnswerUpdate({
        id: question ? question.id : undefined,
        choice: answer,
        durationOfSymptoms: ownToApiSymptomDuration(symptomDuration),
        moreInformationAboutSymptom: symptomInfo,
        isValid: isValid,
      });
    } // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [answer, symptomDuration, symptomInfo, isValid]);

  if (!question || loading) {
    return (
      <div className={classnames('flex', 'flex-col', 'mb-3')}>
        <SkeletonLine width="180px" className={classnames('my-0.5')} />
        <div className={classnames('flex', 'mt-0.5')}>
          {}
          <PillButton className={pillButtonBaseStyle}>
            <SkeletonLine width="60px" className={classnames('my-0.25')} />
          </PillButton>
          <PillButton className={pillButtonBaseStyle}>
            <SkeletonLine width="60px" className={classnames('my-0.25')} />
          </PillButton>
          <PillButton className={pillButtonBaseStyle}>
            <SkeletonLine width="60px" className={classnames('my-0.25')} />
          </PillButton>
        </div>
      </div>
    );
  }

  return (
    <div
      id={`id-question-${question.text?.replace(/\s+/g, '-').toLowerCase()}`}
      className={classnames('flex', 'flex-col', 'mb-2.5', isNotLast && notLastStyles)}
    >
      <h2 className={classnames('mb-1', 'text-style-subtitle-m', error && 'text-attention-red')}>{question.text}</h2>
      {question.details && <p className={classnames('text-zodiac-black-65')}>{question.details}</p>}
      <div className={classnames('flex', 'mb-0.5')}>
        <PillButton
          iconLeft={answer === AnswerChoice.yes ? 'Check' : undefined}
          onClick={() => onClickAnswer(question, AnswerChoice.yes)}
          className={pillButtonBaseStyle}
        >
          {t('yes')}
        </PillButton>
        <PillButton
          iconLeft={answer === AnswerChoice.no ? 'Check' : undefined}
          onClick={() => onClickAnswer(question, AnswerChoice.no)}
          className={pillButtonBaseStyle}
        >
          {t('no')}
        </PillButton>
        <PillButton
          iconLeft={answer === AnswerChoice.dontKnow ? 'Check' : undefined}
          onClick={() => onClickAnswer(question, AnswerChoice.dontKnow)}
          className={pillButtonBaseStyle}
        >
          {t('i_dont_know')}
        </PillButton>
      </div>
      {question.askDurationOfSymptom && answer === AnswerChoice.yes && (
        <div className={classnames('flex', 'flex-col', 'mb-1')}>
          <Text size="m">{t('your_symptoms_duration')}</Text>
          <div>
            <PillButton
              iconLeft={symptomDuration === SymptomDuration.underOne ? 'Check' : undefined}
              onClick={() => onClickDuration(question, SymptomDuration.underOne)}
              className={pillButtonBaseStyle}
            >
              {t('under_one_day')}
            </PillButton>
            <PillButton
              iconLeft={symptomDuration === SymptomDuration.oneToTwo ? 'Check' : undefined}
              onClick={() => onClickDuration(question, SymptomDuration.oneToTwo)}
              className={pillButtonBaseStyle}
            >
              {t('one_to_two_days')}
            </PillButton>
            <PillButton
              iconLeft={symptomDuration === SymptomDuration.twoToThree ? 'Check' : undefined}
              onClick={() => onClickDuration(question, SymptomDuration.twoToThree)}
              className={pillButtonBaseStyle}
            >
              {t('two_to_three_days')}
            </PillButton>
            <PillButton
              iconLeft={symptomDuration === SymptomDuration.overThree ? 'Check' : undefined}
              onClick={() => onClickDuration(question, SymptomDuration.overThree)}
              className={pillButtonBaseStyle}
            >
              {t('over_three_days')}
            </PillButton>
          </div>
        </div>
      )}
      {question.askMoreInformationAboutSymptom && answer === AnswerChoice.yes && (
        <div>
          <TextField
            as="input"
            label={question.instruction ?? t('more_information')}
            id="symptoms-info"
            name="symptoms-info"
            value={symptomInfo ?? ''}
            onChange={e => onClickMoreInformation(question, e.target.value)}
            placeholder={t('write_information')}
            className={classnames('mt-1', 'mb-0')}
          />
        </div>
      )}
    </div>
  );
};
